import type { MediaType, Special, Title } from "@/types";
import { titles } from "@/mocks/titles";
import { specials } from "@/mocks/specials";

export interface TypeMeta {
  label: string;
  en: MediaType;
  description: string;
}

export const typeMetaMap: Record<MediaType, TypeMeta> = {
  movie: {
    label: "电影",
    en: "movie",
    description:
      "聚焦院线与网络电影的最新动态、剧情解析与高分推荐，涵盖剧情、悬疑、科幻、爱情等多种题材，帮你在海量片单里快速找到值得一看的那一部。",
  },
  tv: {
    label: "电视剧",
    en: "tv",
    description:
      "涵盖国产剧、美剧、日剧等多地剧集，汇集剧情介绍、追剧指南与口碑排行，从权谋古装到都市现实，陪你追完一部长剧。",
  },
  variety: {
    label: "综艺",
    en: "variety",
    description:
      "从音乐竞演到生活真人秀，收录当下热门与口碑综艺，轻松获取节目看点、嘉宾阵容与更新动态。",
  },
};

export const allTitles = titles;
export const allSpecials = specials;

export function getTitleById(id?: string): Title | undefined {
  if (!id) return undefined;
  return titles.find((t) => t.id === id);
}

export function getSpecialById(id?: string): Special | undefined {
  if (!id) return undefined;
  return specials.find((s) => s.id === id);
}

export function getTitlesByIds(ids: string[]): Title[] {
  return ids
    .map((id) => getTitleById(id))
    .filter((t): t is Title => Boolean(t));
}

export function getTitlesByType(type: MediaType): Title[] {
  return titles.filter((t) => t.type === type);
}

export function getSpecialsForTitle(title: Title): Special[] {
  return specials.filter((s) => title.specialIds.includes(s.id));
}

export function getRelatedTitles(title: Title, count = 4): Title[] {
  const scored = titles
    .filter((t) => t.id !== title.id)
    .map((t) => {
      let score = 0;
      if (t.type === title.type) score += 3;
      score += t.genres.filter((g) => title.genres.includes(g)).length * 2;
      score += t.tags.filter((g) => title.tags.includes(g)).length;
      return { item: t, score };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity);
  return scored.slice(0, count).map((s) => s.item);
}

export interface FilterOptions {
  region?: string;
  year?: string;
  genre?: string;
  minRating?: number;
}

const ALL = "全部";

export function filterTitles(list: Title[], options: FilterOptions): Title[] {
  return list.filter((t) => {
    if (options.region && options.region !== ALL && t.region !== options.region) return false;
    if (options.year && options.year !== ALL && String(t.year) !== options.year) return false;
    if (options.genre && options.genre !== ALL && !t.genres.includes(options.genre)) return false;
    if (options.minRating && t.rating < options.minRating) return false;
    return true;
  });
}

export type SortKey = "latest" | "popular" | "rating";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按发布时间" },
  { key: "popular", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export function sortTitles(list: Title[], key: SortKey): Title[] {
  const copy = [...list];
  if (key === "latest") {
    copy.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
  } else if (key === "popular") {
    copy.sort((a, b) => b.popularity - a.popularity);
  } else {
    copy.sort((a, b) => b.rating - a.rating);
  }
  return copy;
}

export function searchTitles(keyword: string): Title[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return titles.filter((t) =>
    [t.name, t.synopsis, t.short, t.director, ...t.genres, ...t.tags]
      .join(" ")
      .toLowerCase()
      .includes(kw)
  );
}

export function getRegions(type: MediaType): string[] {
  return Array.from(new Set(getTitlesByType(type).map((t) => t.region)));
}

export function getYears(type: MediaType): number[] {
  return Array.from(new Set(getTitlesByType(type).map((t) => t.year))).sort((a, b) => b - a);
}

export function getGenres(type: MediaType): string[] {
  return Array.from(new Set(getTitlesByType(type).flatMap((t) => t.genres)));
}

export function getHotTitles(count = 6): Title[] {
  return [...titles].sort((a, b) => b.popularity - a.popularity).slice(0, count);
}

export function getLatestTitles(count = 8): Title[] {
  return [...titles].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate)).slice(0, count);
}

export function getTopSpecials(count = 3): Special[] {
  return specials.slice(0, count);
}